import { formatUnits } from 'ethers'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

import { FeePaymentOption } from '@ambire-common/libs/estimate/interfaces'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import shortenAddress from '@ambire-common/utils/shortenAddress'
import GasTankIcon from '@common/assets/svg/GasTankIcon'
import WarningIcon from '@common/assets/svg/WarningIcon'
import Avatar from '@common/components/Avatar'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import Text from '@common/components/Text'
import TokenIcon from '@common/components/TokenIcon'
import useTheme from '@common/hooks/useTheme'
import {
  GasTankAggregatedBalance,
  getFeeOptionBalance,
  getFeeOptionUsdPrice
} from '@common/modules/sign-account-op/components/Estimation/gasTankFeeOptions'
import spacings from '@common/styles/spacings'
import { THEME_TYPES } from '@common/styles/themeConfig'
import flexbox from '@common/styles/utils/flexbox'
import { translateControllerMessage } from '@common/utils/translateControllerMessage'
import useAccountsControllerState from '@web/hooks/useAccountsControllerState'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import useSignAccountOpControllerState from '@web/hooks/useSignAccountOpControllerState'

import getStyles from './styles'

const PayOption = ({
  feeOption,
  amountUsd,
  disabledReason,
  disabledTextAppearance = 'errorText',
  amount,
  hasAmount = true,
  gasTankBalance
}: {
  feeOption: FeePaymentOption
  amountUsd: string
  amount: bigint
  /**
   * False when the controller produced no fee speed for this option, i.e. the
   * fee could not be expressed in this token. `amount` is then a placeholder
   * `0n` and must not be printed as if it were a real quote.
   */
  hasAmount?: boolean
  disabledReason?: string
  disabledTextAppearance?: 'errorText' | 'infoText'
  /** Combined balance of the Gas Tank entries folded into this row (if any). */
  gasTankBalance?: GasTankAggregatedBalance
}) => {
  const { t } = useTranslation()
  const { styles, theme, themeType } = useTheme(getStyles)
  const { accounts } = useAccountsControllerState()
  const { account } = useSelectedAccountControllerState()
  const { networks } = useNetworksControllerState()
  const signAccountOpState = useSignAccountOpControllerState()

  const iconSize = 36

  const paidByAccountData = useMemo(
    () => accounts.find((a) => a.addr === feeOption.paidBy),
    [accounts, feeOption.paidBy]
  )

  const formattedAmount = useMemo(() => {
    if (!hasAmount) return '—'

    return formatDecimals(Number(formatUnits(amount, feeOption.token.decimals)), 'amount')
  }, [amount, hasAmount, feeOption.token.decimals])

  const feeTokenNetworkName = useMemo(() => {
    if (feeOption.token.flags.onGasTank) {
      return t('Gas Tank')
    }

    return networks.find((n) => n.chainId === feeOption.token.chainId)?.name || ''
  }, [feeOption.token.flags.onGasTank, feeOption.token.chainId, networks, t])

  // What is left to pay THIS fee with. For a folded Gas Tank row that is the
  // largest single entry, not the combined balance: the relayer settles a fee
  // against one (token, chain) entry and does not pool them, so showing the sum
  // here promises money the user cannot spend in one go. The combined figure
  // still has a place — the Gas Tank balance panel — just not on a fee row.
  const formattedAvailable = useMemo(() => {
    const balance = gasTankBalance ? gasTankBalance.maxSpendable : getFeeOptionBalance(feeOption)
    const usdPrice = getFeeOptionUsdPrice(feeOption)
    const balanceUsd = gasTankBalance
      ? gasTankBalance.maxSpendableUsd
      : usdPrice !== null
        ? balance * usdPrice
        : null

    if (balanceUsd !== null) return formatDecimals(balanceUsd, 'value')

    return `${formatDecimals(balance, 'amount')} ${feeOption.token.symbol}`
  }, [feeOption, gasTankBalance])

  const warning = useMemo(() => {
    if (!signAccountOpState) return

    return signAccountOpState.warnings.find(
      ({ id }) => id === 'estimation-retry' || id === 'feeTokenPriceUnavailable'
    )
  }, [signAccountOpState])

  const isPaidByAnotherAccount = feeOption.paidBy !== account?.addr

  const paidByLabel = useMemo(() => {
    return paidByAccountData?.preferences.label
  }, [paidByAccountData?.preferences.label])

  if (!paidByAccountData) return null

  return (
    <View
      style={[
        flexbox.directionRow,
        flexbox.alignCenter,
        {
          width: '100%'
        }
      ]}
    >
      <View style={[flexbox.flex1, flexbox.directionRow, flexbox.alignCenter, spacings.mrTy]}>
        {feeOption.token.flags.onGasTank ? (
          <View style={styles.gasTankIconWrapper}>
            <GasTankIcon
              color={themeType === THEME_TYPES.DARK ? '#8B3DFF' : theme.primaryLight}
              width={24}
              height={24}
            />
          </View>
        ) : (
          <TokenIcon
            containerStyle={{
              width: iconSize,
              height: iconSize
            }}
            width={iconSize}
            height={iconSize}
            networkSize={14}
            address={feeOption.token.address}
            chainId={feeOption.token.chainId}
            onGasTank={feeOption.token.flags.onGasTank}
            skeletonAppearance="secondaryBackground"
          />
        )}

        <View style={[flexbox.flex1, spacings.mlTy]}>
          <Text weight="semiBold" fontSize={14} numberOfLines={1}>
            {formattedAmount} {feeOption.token.symbol}{' '}
            {feeOption.token.flags.onGasTank ? (
              <View style={styles.gasTankBadge}>
                <Text fontSize={10} color="white" weight="medium">
                  {t('Gas Tank')}
                </Text>
              </View>
            ) : (
              <Text fontSize={12}>{t('on {{network}}', { network: feeTokenNetworkName })}</Text>
            )}
          </Text>

          {disabledReason ? (
            <Text
              weight="medium"
              fontSize={10}
              numberOfLines={1}
              appearance={disabledTextAppearance}
            >
              {disabledReason}
            </Text>
          ) : (
            <Text appearance="secondaryText" weight="medium" fontSize={10} numberOfLines={1}>
              {t('Fee')}: {formatDecimals(Number(amountUsd), 'value')} · {t('Available')}:{' '}
              {formattedAvailable}
            </Text>
          )}
        </View>
      </View>
      {isPaidByAnotherAccount && (
        <View style={[flexbox.alignEnd]}>
          <View style={[flexbox.directionRow, flexbox.alignCenter]}>
            <Avatar
              size={16}
              address={feeOption.paidBy}
              pfp={feeOption.paidBy}
              style={spacings.prTy}
              isSmart={false}
              displayTypeBadge={false}
            />
            <Text fontSize={10} weight="semiBold" numberOfLines={1}>
              {paidByLabel}
            </Text>
          </View>
          <Text fontSize={10} weight="medium">
            {shortenAddress(feeOption.paidBy, 13)}
          </Text>
        </View>
      )}
      {warning && (
        <WarningIcon
          width={20}
          height={20}
          style={spacings.mlTy}
          dataSet={createGlobalTooltipDataSet({
            id: 'estimation-warning',
            content: translateControllerMessage(warning.title)
          })}
          color={theme.warningText}
        />
      )}
    </View>
  )
}

export default React.memo(PayOption)
